import { DocumentReference, Timestamp } from "@um/contracts/firestore";
import {
  Agent,
  AnimalAgent,
  DatabaseRecord,
  ImageReference,
  Mind,
  Metric,
  AgentBase,
  UserAgent,
  HandleJobTask,
} from ".";

export interface Job<
  Tstate = any,
  Tref = {
    [key: string]: DocumentReference | DocumentReference[];
  },
  Tactions = any,
> extends DatabaseRecord {
  name: string;
  handlerName: string;
  references?: Tref;
  timezone?: string;

  // triggers
  trackUpdatesFor?: DocumentReference[];
  trackNewFor?: string[]; // CollectionReference is not supported as a firebase field type
  schedule?: string;
  rruleSchedule?: string;
  scheduleDisplay?: string;
  next: Timestamp | null;
  nextScheduled: Timestamp | null;
  triggerFilters?: {
    [key: string]: {
      propertyChanges: string[];
    };
  };

  actionsAvailible?: {
    [p in keyof Tactions]: boolean;
  };

  metric?: DocumentReference<Metric>;

  parent?: DocumentReference<Job>;
  start?: Timestamp;
  end?: Timestamp;
  agent: AnimalAgent | AgentBase; // the character running the job
  mind?: DocumentReference<Mind>; // the mind that the job is running for
  status: JobStatus;
  /**
   * Set when the job was paused automatically (e.g. `"subscription-lapsed"` when the
   * account's basic entitlement ended) so automation can safely resume only the jobs
   * it paused, leaving manually paused jobs untouched. Cleared on resume.
   */
  pausedReason?: string;
  result?: JobResult;
  state: Tstate;
  history: JobHistory[];
  errorCount?: number;
  agentMessage?: string;
  nextMessage?: string;
  nextScheduledMessage?: string;
}

/**
 * `Job.pausedReason` marker for jobs paused because their assigned character
 * was deleted. The UI blocks resume until a new character is assigned, and
 * subscription automation ignores these (it only resumes its own marker).
 */
export const CHARACTER_DELETED_PAUSE_REASON = "character-deleted";

export type JobStatus = "review" | "active" | "completed" | "cancelled" | "paused";
export type JobResult = "success" | "failure" | "skip";

export type JobHistory = {
  date: Timestamp;
  message: string;
  agent?: Agent;
  image?: ImageReference;
  // When `image` is a paneled expression sheet, identifies the panel
  // (e.g. "A16") the renderer should crop and display.
  imagePanelKey?: string;
  target?: DocumentReference;
  state?: any;
  isError?: boolean;
  task?: DocumentReference<HandleJobTask>;
};

/**
 * Job handlers that count as "a daily routine".
 *
 * Lives here because three places need the same answer: the crew card counts by
 * it, the quest ladder's routine step derives from it, and the admin portal
 * reports on it. A second copy would silently strand any handler it missed.
 */
export const ROUTINE_HANDLER_NAMES = new Set([
  "dailyBoosts",
  "storyTeller",
  "dailyDigest",
  "medicationReminder",
  "routineChecklist",
]);

